import { useMemo } from 'react'

import {
  useFilters, useModals, useNumericParams, useSetState,
} from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'
import type { PageContextPayload } from '@contexts/pageContext'

import CreateOrEditAgencyPortalModal from '@modals/CreateOrEditAgencyPortalModal'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import PageContent from './components/PageContent'

const defaultState = {
  showCreateOrEditAgencyPortalModal: false,
}

const callbacks = (
  component: keyof typeof componentCallbacks,
  setState: (payload: Partial<typeof defaultState>) => void,
) => {
  const componentCallbacks = {
    CreateOrEditAgencyPortalModal: {
      closeModal: () => setState({ showCreateOrEditAgencyPortalModal: false }),
      createAgencyPortal: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      generateAgencyPortalAuthToken: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateAgencyPortal: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const Details = () => {
  const { portalId } = useNumericParams()

  const [state, setState] = useSetState(defaultState)
  const { showCreateOrEditAgencyPortalModal } = state

  const {
    callbacks: filterCallbacks,
    defaultFilters,
  } = useFilters()

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      ...filterCallbacks,
      showCreateOrEditAgencyPortalModal: (payload: object) => {
        setModalData('CreateOrEditAgencyPortalModal', payload)
        setState({ showCreateOrEditAgencyPortalModal: true })
      },
      updatePortal: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    defaultFilters,
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent portalId={portalId} />

        <CreateOrEditAgencyPortalModal
          callbacks={callbacks('CreateOrEditAgencyPortalModal', setState)}
          showModal={showCreateOrEditAgencyPortalModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Details
